import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const DEFAULT_HERO_IMAGE = 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/dota_react/heroes/default.png';

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${props => props.$loading ? 0 : 1};
  transition: opacity 0.2s ease-in-out;
  filter: ${props => props.$grayscale ? 'grayscale(70%) brightness(1.2)' : 'none'};
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.2);
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.2s ease-in-out;
`;

const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #1a1a1a;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 20px;
`;

// Get size value in pixels based on size prop
const getSizeValue = (size) => {
  switch (size) {
    case 'small':
      return '40px';
    case 'medium':
      return '60px';
    case 'large':
      return '100px';
    default:
      return '60px';
  }
};

/**
 * HeroImage Component
 * 
 * Displays a hero image with the specified size
 * 
 * @param {Object} props - Component props
 * @param {Object} props.hero - Hero object with name and image properties
 * @param {string} props.size - Size of the hero image ('small', 'medium', 'large')
 * @param {boolean} props.$grayscale - Whether to apply grayscale effect
 */
const HeroImage = ({ hero, size = 'medium', $grayscale = false }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(false);

    // Reset image URL when hero changes
    if (!hero) {
      setImageUrl(null);
      return;
    }

    // Use the provided imageUrl or construct one from the hero id/name
    const url = hero.imageUrl || 
      `https://cdn.cloudflare.steamstatic.com/apps/dota2/images/dota_react/heroes/${hero.id || hero.name.toLowerCase().replace(/\s/g, '_')}.png`;

    // Preload the image
    const img = new Image();
    img.src = url;

    img.onload = () => {
      setImageUrl(url);
      setLoading(false);
    };

    img.onerror = () => {
      console.warn(`Failed to load hero image for ${hero.name}, using default`);
      setImageUrl(DEFAULT_HERO_IMAGE);
      setError(true);
      setLoading(false);
    };

    // Cleanup
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [hero]);

  if (!hero) return null;
  
  return (
    <ImageContainer>
      {imageUrl && (
      <StyledImage 
          src={imageUrl}
          alt={hero.name}
          $loading={loading}
          $grayscale={$grayscale}
        />
      )}
      <LoadingOverlay $visible={loading}>
        <LoadingSpinner />
      </LoadingOverlay>
      {error && <ErrorPlaceholder>?</ErrorPlaceholder>}
    </ImageContainer>
  );
};

export default React.memo(HeroImage);
